<template>
  <div id="listWidget" class="mb-5">
    <!-- 사용 안내 -->
    <div class="instruction-card card-item mb-3">
      <div class="instruction-header">
        <i class="fa-solid fa-info-circle me-2 text-primary"></i>
        <span class="instruction-title">사용 안내</span>
      </div>
      <div class="instruction-body">
        <p class="mb-0" style="font-size: 0.85rem; line-height: 1.6; color: #495057;">
          가상버튼을 추가해주세요. 포함할 장치의 <code style="font-size: 100%">포함</code>/<code style="font-size: 100%">미포함</code>과
          상태값을 설정하고 <code style="font-size: 100%">장치이름</code>을 선택해주세요.
          설정을 완료하고 <code style="font-size: 100%">등록 버튼</code>을 클릭하면 설정된 상태값으로 가상버튼이 생성됩니다.
        </p>
        <p class="mb-0 mt-2" style="font-size: 0.8rem; line-height: 1.5; color: #6c757d;">
          다른 방의 조명·커튼도 아래에서 방 이름을 눌러 펼친 뒤
          <code style="font-size: 100%">포함</code>/<code style="font-size: 100%">미포함</code>으로 선택할 수 있습니다. (다른 제어기 장치는 네트워크가 필요합니다.)
        </p>
      </div>
    </div>

    <!-- 장치 이름 + 등록 버튼 -->
    <div class="name-edit-card card-item mb-3" v-if="realDevices.length > 0 || selectedRoomLights.length > 0 || selectedOtherLights.length > 0 || selectedCurtainDevices.length > 0 || otherRoomGroups.length > 0">
      <div class="name-edit-body d-flex align-items-center">
        <div class="menu-search-input flex-fill" style="padding-right: 10px">
          <select class="form-control" v-model="deviceName">
            <option value="" style="display: none">장치이름을 선택해주세요.</option>
            <option v-for="index in 99" :key="index" :value="`${index}번`" :disabled="isExistIndex(index)">{{ index }}번</option>
          </select>
        </div>
        <button class="btn btn-primary" @click="onRegistClick">등록</button>
      </div>
    </div>

    <!-- 조명 (현재 방) -->
    <div class="section-card card-item mb-3" v-if="realDevices.length > 0">
      <div class="section-header">
        <i class="fa-regular fa-lightbulb me-2 text-primary"></i>
        <span class="section-title">조명 ( {{ group?.name }} )</span>
      </div>
      <div class="section-body">
        <div class="row">
          <VirtualDeviceItem
            v-for="item in realDevices"
            :key="item._id"
            :group="group"
            :device="item"
            deviceType="1"
            optional-include
            :selectedDevices="selectedRoomLights"
            @onDeviceChecked="handleRoomLightChecked"
          />
        </div>
      </div>
    </div>

    <!-- 커튼 (현재 방) -->
    <div class="section-card card-item mb-3" v-if="curtainDevices.length > 0">
      <div class="section-header">
        <img :src="require('@/assets/images/curtain.svg')" class="section-header-icon me-2" alt="" />
        <span class="section-title">커튼 ( {{ group?.name }} )</span>
      </div>
      <div class="section-desc-block">
        <i class="fa-solid fa-info-circle me-2 text-primary section-desc-icon"></i>
        <span class="section-desc">가상버튼이 ON일 때 포함한 커튼의 열림/닫힘 정도가 적용됩니다.</span>
      </div>
      <div class="section-body">
        <div class="row">
          <VirtualDeviceItem
            v-for="item in curtainDevices"
            :key="item._id"
            :group="group"
            :device="item"
            :deviceType="item.type"
            :selectedDevices="selectedCurtainDevices"
            @onDeviceChecked="handleCurtainChecked"
          />
        </div>
      </div>
    </div>

    <!-- 다른 방 (조명 + 커튼) -->
    <div
      v-for="og in otherRoomGroups"
      :key="og._id"
      class="section-card card-item mb-3"
    >
      <div
        class="section-header section-header-toggle"
        :class="{ 'section-header-collapsed': !isOtherRoomExpanded(og._id) }"
        role="button"
        @click="toggleOtherRoom(og._id)"
      >
        <i class="fa-brands fa-microsoft me-2 text-primary"></i>
        <span class="section-title flex-fill">{{ og.name }}</span>
        <span
          v-if="selectedCountInGroup(og) > 0"
          class="other-room-badge me-2"
        >포함 {{ selectedCountInGroup(og) }}</span>
        <i
          :class="`fa-solid ${isOtherRoomExpanded(og._id) ? 'fa-chevron-up' : 'fa-chevron-down'} text-muted`"
          style="font-size: 0.85rem;"
        ></i>
      </div>
      <div v-if="isOtherRoomExpanded(og._id)" class="section-body">
        <div class="row" v-if="physicalLampsOf(og).length > 0">
          <VirtualDeviceItem
            v-for="item in physicalLampsOf(og)"
            :key="item._id"
            :group="og"
            :device="item"
            deviceType="1"
            optional-include
            :selectedDevices="selectedOtherLights"
            @onDeviceChecked="handleOtherLightChecked"
          />
        </div>
        <div
          v-if="curtainDevicesOf(og).length > 0"
          class="section-desc-block"
          :class="{ 'mt-2': physicalLampsOf(og).length > 0 }"
          style="margin-left: -1rem; margin-right: -1rem;"
        >
          <i class="fa-solid fa-info-circle me-2 text-primary section-desc-icon"></i>
          <span class="section-desc">커튼 · 포함 시 가상버튼 ON 때 열림 정도가 적용됩니다.</span>
        </div>
        <div class="row" v-if="curtainDevicesOf(og).length > 0">
          <VirtualDeviceItem
            v-for="item in curtainDevicesOf(og)"
            :key="item._id"
            :group="og"
            :device="item"
            :deviceType="item.type"
            :selectedDevices="selectedCurtainDevices"
            @onDeviceChecked="handleCurtainChecked"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  ref,
  reactive,
  getCurrentInstance,
  onBeforeMount,
  computed,
  onMounted,
  onBeforeUnmount,
  watch,
} from 'vue';
import Axios, { DeviceApi, GroupApi } from '@/api';
import {
  useRouter,
  useRoute,
  onBeforeRouteLeave,
  onBeforeRouteUpdate,
} from 'vue-router';
import { useStore } from 'vuex';
import PopDialog from '@/common/components/popup/PopDialog.vue';
import useModal from '@/common/hooks/useModal';
import VirtualDeviceItem from './components/VirtualDeviceItem.vue';
import useMqttControl from '@/common/hooks/useMqttControl';
import {
  MQTTCurtainCommand,
  MQTTDooyaRonoCommand,
  MQTTDooyaRonoAction,
  isDooyaRonoClosePercentSet,
  getDooyaRonoCloseCtrlData,
} from '@/common/constants';

const mqttControl = reactive(useMqttControl());
const { emitter } = getCurrentInstance().appContext.config.globalProperties;
const router = useRouter();
const route = useRoute();
const store = useStore();
const deviceName = ref('');
const srcDevices = ref([]);
const deviceType = ref(0);
const selectedCurtainDevices = ref([]);
const selectedRoomLights = ref([]);
const selectedOtherLights = ref([]);
const expandedOtherRoomIds = ref([]);

const zones = computed(() => {
  return store.getters['home/getZones'];
});

const groups = computed(() => {
  const groups = [];
  zones?.value?.forEach((zone) => groups.push(...zone.groups));
  return groups;
});

const group = computed(() => {
  return groups?.value?.find((g) => g._id == route.params.groupId);
});

const currentZone = computed(() => {
  const gid = String(route.params.groupId);
  return zones.value?.find((z) => (z.groups || []).some((g) => String(g._id) === gid));
});

const physicalLampsOf = (g) => {
  return (g?.devices || []).filter(
    (d) => d.type === 1 && (d.isVirtual === undefined || d.isVirtual === false),
  );
};

const curtainDevicesOf = (g) => {
  return (g?.devices || []).filter(
    (d) =>
      (d.type === 3 || d.type === 5 || d.type === 6) &&
      (d.isVirtual === undefined || d.isVirtual === false),
  );
};

const otherRoomGroups = computed(() => {
  const gid = String(route.params.groupId);
  return (currentZone.value?.groups || []).filter(
    (g) =>
      String(g._id) !== gid &&
      (physicalLampsOf(g).length > 0 || curtainDevicesOf(g).length > 0),
  );
});

const allOtherRoomLamps = computed(() => {
  const list = [];
  for (const og of otherRoomGroups.value) {
    list.push(...physicalLampsOf(og));
  }
  return list;
});

const allOtherRoomCurtains = computed(() => {
  const list = [];
  for (const og of otherRoomGroups.value) {
    list.push(...curtainDevicesOf(og));
  }
  return list;
});

const devices = computed(() => {
  return group?.value?.devices.filter(item => item.type == 1) || [];
});

const curtainDevices = computed(() => {
  return (group?.value?.devices || []).filter(
    (item) =>
      (item.type == 3 || item.type == 5 || item.type == 6) &&
      (item.isVirtual === undefined || item.isVirtual === false),
  );
});

const realDevices = computed(() => {
  return devices?.value.filter(item => item.isVirtual === undefined || item.isVirtual === false) || [];
});

const existIndices = computed(() => {
  const regex = /[^0-9]/g;
  const result = devices?.value?.filter((item) => item.name).map((item) => parseInt(item.name.replace(regex, ""))) || []
  return result;
});

const isOtherRoomExpanded = (groupId) => expandedOtherRoomIds.value.includes(String(groupId));

const toggleOtherRoom = (groupId) => {
  const sid = String(groupId);
  if (expandedOtherRoomIds.value.includes(sid)) {
    expandedOtherRoomIds.value = expandedOtherRoomIds.value.filter((id) => id !== sid);
  } else {
    expandedOtherRoomIds.value = [...expandedOtherRoomIds.value, sid];
  }
};

const selectedCountInGroup = (og) => {
  const lampIds = new Set(physicalLampsOf(og).map((d) => String(d._id)));
  const curtainIds = new Set(curtainDevicesOf(og).map((d) => String(d._id)));
  const lightCount = selectedOtherLights.value.filter((d) => lampIds.has(String(d._id))).length;
  const curtainCount = selectedCurtainDevices.value.filter((d) => curtainIds.has(String(d._id))).length;
  return lightCount + curtainCount;
};

const liveDeviceFromStore = (deviceId) => {
  const sid = String(deviceId);
  for (const z of zones.value || []) {
    for (const g of z.groups || []) {
      const found = (g.devices || []).find((d) => String(d._id) === sid);
      if (found) return found;
    }
  }
  return null;
};

const resolveSelectedRoomLights = () => {
  return selectedRoomLights.value.map((sel) => {
    const live = liveDeviceFromStore(sel._id);
    return live ? { ...live } : { ...sel };
  });
};

const resolveSelectedOtherLights = () => {
  return selectedOtherLights.value.map((sel) => {
    const live = liveDeviceFromStore(sel._id);
    return live ? { ...live } : { ...sel };
  });
};

const resolveSelectedCurtains = () => {
  return selectedCurtainDevices.value.map((sel) => {
    const live = liveDeviceFromStore(sel._id);
    return live ? { ...live } : { ...sel };
  });
};

const buildMotorRestoreData = (item) => {
  const addr = parseInt(item?.address, 10);
  if (Number.isNaN(addr)) {
    return null;
  }
  if (isDooyaRonoClosePercentSet(item)) {
    return {
      addr,
      action: MQTTDooyaRonoAction.Percent,
      percent: parseInt(item.percent, 10),
    };
  }
  return getDooyaRonoCloseCtrlData(item, item?.close === true);
};

const publishStatus = async (devices) => {
  console.log("devices:", devices)

  const lampItems = (devices || []).filter((d) => d.type == 1);
  const byController = {};
  for (const it of lampItems) {
    const cid = it.controllerId;
    if (!byController[cid]) byController[cid] = [];
    byController[cid].push(it);
  }
  for (const controllerId of Object.keys(byController)) {
    const group = byController[controllerId];
    const portsArr = [];
    const onoffArr = [];
    const brightArr = [];
    const colorArr = [];
    const deviceIds = [];
    for (const it of group) {
      deviceIds.push(it._id);
      const ports = Array.isArray(it.item?.ports) ? it.item.ports : (it.item?.ports != null ? [it.item.ports] : []);
      const onoff = it.item?.onoff ?? 0;
      const bright = it.item?.bright != null ? parseInt(it.item.bright, 10) : 0;
      const color = it.item?.color ?? 0;
      for (const p of ports) {
        portsArr.push(p);
        onoffArr.push(onoff);
        brightArr.push(bright);
        colorArr.push(color);
      }
    }
    if (portsArr.length > 0) {
      mqttControl.onPublishBatch(controllerId, deviceIds, {
        serial: controllerId,
        cmd: 'cs',
        type: 1,
        data: { ports: portsArr, onoff: onoffArr, bright: brightArr, color: colorArr },
      });
    }
  }

  for (const device of devices || []) {
    if (device.type == 3) {
      mqttControl.onPublishTemp(device.controllerId, MQTTCurtainCommand.IsSetClosePercent, `${device.item.address},${device.item.percent}`);
      await requestUpdateDevice(device._id, {
        item: { percent: device.item.percent }
      });
    } else if (device.type == 5 || device.type == 6) {
      const data = buildMotorRestoreData(device.item);
      if (!data) {
        continue;
      }
      mqttControl.onPublish(device.controllerId, device._id, {
        cmd: device.type == 5 ? MQTTDooyaRonoCommand.DooyaCtrl : MQTTDooyaRonoCommand.RonoCtrl,
        type: 1,
        data,
      });
      await requestUpdateDevice(device._id, {
        item: { percent: device.item.percent }
      });
    }
  }
};

onBeforeMount(() => {
  // if (route.path.indexOf('light') >= 0) {
  //   deviceType.value = 1;
  // } else if (route.path.indexOf('curtain') >= 0) {
  //   deviceType.value = 3;
  // }
  // console.log(deviceType.value);
});

onMounted(() => {
  srcDevices.value = JSON.parse(JSON.stringify([
    ...realDevices.value,
    ...allOtherRoomLamps.value,
    ...curtainDevices.value,
    ...allOtherRoomCurtains.value,
  ]));
});

// 페이지를 떠나기 전에 원래 상태로 복원
onBeforeRouteLeave(async (to, from, next) => {
  // if (rollbackStatus) {
    try {
      console.log('페이지를 떠나기 전 원래 상태로 복원 중...');
      await publishStatus(srcDevices.value);
      console.log('원래 상태 복원 완료');
    } catch (error) {
      console.error('원래 상태 복원 실패:', error);
    }
  // }
  next(); // 페이지 이동 허용
});

const handleCurtainChecked = (data) => {
  if (data.isChecked) {
    const exists = selectedCurtainDevices.value.some(
      (device) => String(device._id) === String(data.device._id),
    );
    if (!exists) {
      selectedCurtainDevices.value.push(data.device);
    }
  } else {
    selectedCurtainDevices.value = selectedCurtainDevices.value.filter(
      (device) => String(device._id) !== String(data.device._id),
    );
  }
};

const handleRoomLightChecked = (data) => {
  if (data.isChecked) {
    const exists = selectedRoomLights.value.some((d) => String(d._id) === String(data.device._id));
    if (!exists) {
      selectedRoomLights.value.push(data.device);
    }
  } else {
    selectedRoomLights.value = selectedRoomLights.value.filter(
      (d) => String(d._id) !== String(data.device._id),
    );
  }
};

const handleOtherLightChecked = (data) => {
  if (data.isChecked) {
    const exists = selectedOtherLights.value.some((d) => String(d._id) === String(data.device._id));
    if (!exists) {
      selectedOtherLights.value.push(data.device);
    }
  } else {
    selectedOtherLights.value = selectedOtherLights.value.filter(
      (d) => String(d._id) !== String(data.device._id),
    );
  }
};

const isExistIndex = (index) => {
  const result = existIndices?.value?.filter(item => item === index) || [];
  return result.length > 0;
};

const onRegistClick = () => {
  if (deviceName.value.length === 0) {
    emitter.emit('showPopAlert', {
      description: '장치 이름을 선택해주세요.',
    });
    return;
  }

  const roomLights = resolveSelectedRoomLights();
  const otherLights = resolveSelectedOtherLights();
  const includedCurtains = resolveSelectedCurtains();

  if (roomLights.length === 0 && otherLights.length === 0 && includedCurtains.length === 0) {
    emitter.emit('showPopAlert', {
      description: '포함할 조명 또는 커튼을 1개 이상 선택해주세요.',
    });
    return;
  }

  const hostControllerId =
    roomLights[0]?.controllerId ||
    otherLights[0]?.controllerId ||
    includedCurtains[0]?.controllerId;

  const params = {
    controllerId: hostControllerId,
    name: deviceName.value,
    type: 1,
    isVirtual: 1,
    virtualItems: [...roomLights, ...otherLights, ...includedCurtains],
  };
  console.log(params);
  requestAddDevice(params);
};

const requestAddDevice = async (payload) => {
  try {
    const result = await Axios.post(
      GroupApi.REGIST_GROUP_DEVICES.replace(':id', group.value._id),
      payload,
    );
    if (result.resultCode === '0000') {
      deviceName.value = '';
      await store.dispatch('home/fetchAllZones');
      emitter.emit('showPopAlert', {
        description: '가상 버튼이 등록되었습니다.',
        okCallback: () => router.go(-1),
        cancelCallback:() => router.go(-1)
      });
      return;
    }

    emitter.emit('showPopAlert', {
      description: result.resultMessage,
    });
  } catch (e) {
    console.log(e);
    emitter.emit('showPopAlert', {
      description: '장치 추가중 오류가 발생하였습니다.',
    });
  }
}; 

const requestUpdateDevice = async (id, params) => {
  return new Promise(async (resolve, reject) => {
    try {
      const result = await Axios.patch(DeviceApi.UPDATE_DEVICE.replace(':id', id), params);
      if (result.resultCode === '0000') {
        store.commit('home/updateDevice', {
          groupId: group.value._id,
          id,
          data: params,
        });
        resolve(result);
      } else {
        // emitter.emit('showPopAlert', {
        //   description: result.resultMessage,
        // });
        reject(result.resultMessage);
      }
    } catch (error) {
      // emitter.emit('showPopAlert', {
      //   description: '장치 정보 수정에 실패했습니다.',
      // });
      reject('장치 정보 수정에 실패했습니다.');
    }
  });
};
</script>

<style scoped>
/* instruction-card 스타일 (VirtualButtonUpdatePage와 동일) */
.instruction-card {
  background-color: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 1.2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  transition: box-shadow 0.3s ease;
}

.instruction-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.instruction-header {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}

.instruction-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: #212529;
}

.instruction-body {
  padding: 0.75rem 1rem;
}

/* 장치 이름 + 등록 버튼 카드 */
.name-edit-card {
  background-color: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 1.2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  transition: box-shadow 0.3s ease;
}

.name-edit-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.name-edit-body {
  padding: 0.75rem 1rem;
}

/* 조명/커튼 섹션 카드 */
.section-card {
  background-color: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 1.2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  transition: box-shadow 0.3s ease;
}

.section-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.section-header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}

.section-header-icon {
  height: 1em;
  width: auto;
  vertical-align: middle;
  filter: brightness(0) saturate(100%) invert(40%) sepia(99%) saturate(3200%) hue-rotate(216deg) brightness(94%) contrast(102%);
}

.section-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: #212529;
}

.section-desc-block {
  display: flex;
  align-items: flex-start;
  gap: 0;
  padding: 0.4rem 1rem;
  border-bottom: 1px solid #e9ecef;
}

.section-desc-icon {
  font-size: 0.85rem;
  flex-shrink: 0;
  margin-top: 0.1rem;
}

.section-desc {
  font-size: 0.85rem;
  line-height: 1.6;
  color: #495057;
  word-break: keep-all;
}

.section-body {
  padding: 0.75rem 1rem;
}

.section-header-toggle {
  cursor: pointer;
  user-select: none;
}

.section-header-collapsed {
  border-bottom: none;
}

.other-room-badge {
  font-size: 0.75rem;
  font-weight: 600;
  color: #0d6efd;
  background: #e7f1ff;
  border-radius: 999px;
  padding: 0.15rem 0.5rem;
}
</style>
